/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */
// Add data types to window.navigator for use in this file. See https://www.typescriptlang.org/docs/handbook/triple-slash-directives.html#-reference-types- for more info.
/// <reference types="user-agent-data-types" />

export class UseragentUtil {
   private userAgentString: string = null;
   private browserName: string = BrowserName.UNKNOWN;
   private browserFullVersion: string = null;
   private hostOS: string = ClientOS.UNKNOWN;
   private bitness: string = null;

   constructor() {
      const uad = navigator.userAgentData;
      if (uad && uad.platform && Array.isArray(uad.brands) && uad.brands.length > 0) {
         const platform = uad.platform.toLowerCase();
         for (let b of uad.brands) {
            // Poll browser type
            let brand = b.brand.toLowerCase();
            if (brand.indexOf("chrome") !== -1) {
               this.browserName = BrowserName.CHROME;
            } else if (brand.indexOf("edge") !== -1) {
               this.browserName = BrowserName.CHROMIUMEDGE;
            } else if (brand.indexOf("opera") !== -1) {
               this.browserName = BrowserName.OPERA;
            } else if (brand.indexOf("safari") !== -1) {
               // Although it's not supported on Safari/Firefox yet
               // But in case one day it's available, add logical branch as well
               this.browserName = BrowserName.SAFARI;
            } else if (brand.indexOf("firefox") !== -1) {
               this.browserName = BrowserName.FIREFOX;
            }
         }
         // Get host os type
         if (platform.indexOf("windows") !== -1) {
            this.hostOS = ClientOS.WINDOWS;
         } else if (platform.indexOf("android") !== -1) {
            this.hostOS = ClientOS.ANDROID;
         } else if (platform.indexOf("linux") !== -1) {
            this.hostOS = ClientOS.LINUX;
         } else if (platform.indexOf("chrome os") !== -1) {
            this.hostOS = ClientOS.CHROMEOS;
         } else if (platform.indexOf("macos") !== -1) {
            this.hostOS = ClientOS.MAC;
         } else if (platform.indexOf("i") == 0) {
            // This is not supported on IOS now, but in case it's available
            // Assume the platform will be ios, iphone, ipados
            this.hostOS = ClientOS.IOS;
         }
      }

      this.userAgentString = navigator.userAgent;
      const ua = this.userAgentString.toLowerCase();

      if (this.browserName == BrowserName.UNKNOWN) {
         if (ua.indexOf("airwatch") !== -1) {
            // AirWatch does not support UserAentData API
            this.browserName = BrowserName.AIRWATCH;
         } else if (ua.indexOf("msie") !== -1 || ua.indexOf("trident") !== -1 || ua.indexOf("edge") !== -1) {
            //In viewclient-model, mIsIE is true on legacy edge browser, so it keeps there
            this.browserName = BrowserName.IE;
         } else if (ua.indexOf("opera/") !== -1 || ua.indexOf("opr/") !== -1) {
            this.browserName = BrowserName.OPERA;
         } else if (ua.indexOf("firefox") !== -1 || ua.indexOf("iceweasel") !== -1) {
            this.browserName = BrowserName.FIREFOX;
         } else if (ua.indexOf("applewebkit") !== -1) {
            if (ua.indexOf("edg/") !== -1) {
               this.browserName = BrowserName.CHROMIUMEDGE;
            } else if (ua.indexOf("chrome") !== -1 || ua.indexOf("crios") !== -1) {
               this.browserName = BrowserName.CHROME;
            } else if (ua.indexOf("safari") !== -1) {
               this.browserName = BrowserName.SAFARI;
            }
         }
      }
      if (this.hostOS == ClientOS.UNKNOWN) {
         if (ua.indexOf("windows") !== -1) {
            this.hostOS = ClientOS.WINDOWS;
         } else if (ua.indexOf("android") !== -1) {
            this.hostOS = ClientOS.ANDROID;
         } else if (ua.indexOf("cros") !== -1) {
            this.hostOS = ClientOS.CHROMEOS;
         } else if (ua.indexOf("linux") > -1) {
            this.hostOS = ClientOS.LINUX;
         } else if (ua.indexOf("iphone") !== -1 || ua.indexOf("ipad") !== -1) {
            this.hostOS = ClientOS.IOS;
         } else if (ua.indexOf("macos") !== -1 || ua.indexOf("macintosh") !== -1) {
            this.hostOS = ClientOS.MAC;
         }
      }
   }

   public getHostOS = (): string => {
      return this.hostOS;
   };

   public getBrowserName = (): string => {
      return this.browserName;
   };

   public getBrowserVersionFull = async (): Promise<string> => {
      if (!this.browserFullVersion) {
         if (navigator.userAgentData) {
            let u = await navigator.userAgentData.getHighEntropyValues(["fullVersionList"]);
            for (let b of u.fullVersionList) {
               let brand = b.brand.toLowerCase();
               if (brand.indexOf("chromium") == -1 && brand.indexOf("not") !== 0) {
                  this.browserFullVersion = b.version;
                  break;
               }
            }
         }
         if (!this.browserFullVersion) {
            this.userAgentString = navigator.userAgent;
            let ua = this.userAgentString.toLowerCase();
            if (this.browserName == BrowserName.IE) {
               const vs = window.navigator.appVersion.toString();
               if (ua.indexOf("msie") > -1) {
                  this.browserFullVersion = ua.match(/(msie)\/?\s*(\.?\d+(\.\d+)*)/i)[2];
               } else if (ua.indexOf("trident") > -1) {
                  this.browserFullVersion = ua.match(/rv[:\s]([\w.]+)/i)[1];
               } else if (ua.indexOf("edge") > -1) {
                  //In viewclient-model, mVersionIE is legacy edge version
                  this.browserFullVersion = ua.match(/(edge)\/?\s*(\.?\d+(\.\d+)*)/i)[2];
               }
            } else if (this.browserName == BrowserName.CHROMIUMEDGE) {
               this.browserFullVersion = ua.match(/(edg)\/?\s*(\.?\d+(\.\d+)*)/i)[2];
            } else if (this.browserName == BrowserName.SAFARI) {
               this.browserFullVersion = ua.match(/version\/([.\d]+)/i)[1];
            } else if (this.browserName == BrowserName.OPERA) {
               if (ua.indexOf("opera") !== -1) {
                  this.browserFullVersion = ua.match(/(opera)\/?\s*(\.?\d+(\.\d+)*)/i)[2];
               } else if (ua.indexOf("opr/") !== -1) {
                  this.browserFullVersion = ua.match(/(opr)\/?\s*(\.?\d+(\.\d+)*)/i)[2];
               }
            } else if (this.browserName == BrowserName.CHROME) {
               if (ua.indexOf("chrome") !== -1) {
                  this.browserFullVersion = ua.match(/(chrome)\/?\s*(\.?\d+(\.\d+)*)/i)[2];
               } else if (ua.indexOf("crios") !== -1) {
                  this.browserFullVersion = ua.match(/(crios)\/?\s*(\.?\d+(\.\d+)*)/i)[2];
               }
            } else if (this.browserName == BrowserName.FIREFOX) {
               this.browserFullVersion = ua.match(/(firefox)\/?\s*(\.?\d+(\.\d+)*)/i)[2];
            } else if (this.browserName == BrowserName.AIRWATCH) {
               this.browserFullVersion = ua.match(/(airwatch browser)\s*(v\.?\d+(\.\d+)*)/i)[2].replace("v", "");
            }
         }
      }
      return this.browserFullVersion;
   };

   public getUserAgent = (): string => {
      if (!this.userAgentString) {
         this.userAgentString = navigator.userAgent;
      }
      return this.userAgentString;
   };

   public isMobileVr = (): boolean => {
      this.getUserAgent();
      return this.userAgentString.toLowerCase().indexOf("mobile vr") > -1;
   };

   public isIphone = (): boolean => {
      let _isIphone = false;
      if (this.hostOS == ClientOS.IOS) {
         this.getUserAgent();
         _isIphone = this.userAgentString.toLowerCase().indexOf("iphone") > -1;
      }
      return _isIphone;
   };

   public getWebkitVersion = (): string => {
      this.getUserAgent();
      return this.userAgentString.match(/webkit\/([.\d]+)/i)[1];
   };

   /**
    * Only distinguish 64/32 bit system on Windows and Linux
    */
   public getOSBitness = async (): Promise<string> => {
      let bitness = "";
      if (navigator.userAgentData) {
         let u = await navigator.userAgentData.getHighEntropyValues(["bitness"]);
         if (u && u.bitness && u.bitness !== "64") {
            bitness = "32";
         }
      }
      if (!bitness) {
         let ua = navigator.userAgent.toLowerCase();
         if (ua.indexOf("windows") > -1 && ua.indexOf("wow64") == -1 && ua.indexOf("win64") == -1) {
            bitness = "32";
         } else if (ua.indexOf("linux") > -1 && ua.indexOf("x86_64") == -1) {
            bitness = "32";
         }
      }
      this.bitness = bitness ? bitness : "64";
      return this.bitness;
   };
}

export enum ClientOS {
   WINDOWS = "WINDOWS",
   LINUX = "LINUX",
   ANDROID = "ANDROID",
   IOS = "IOS",
   CHROMEOS = "CHROMEOS",
   MAC = "MAC",
   UNKNOWN = "UNKNOWN"
}

export enum BrowserName {
   CHROME = "CHROME",
   CHROMIUMEDGE = "CHROMIUMEDGE",
   SAFARI = "SAFARI",
   FIREFOX = "FIREFOX",
   OPERA = "OPERA",
   IE = "IE",
   AIRWATCH = "AIRWATCH",
   UNKNOWN = "UNKOWN"
}

export const useragentUtil = new UseragentUtil();
